import { AxiosInstance } from 'axios';
import { Category } from 'enum/categories';
import { EventStatus } from 'enum/event-status';
import { SettingApi } from 'enum/settings';
import { ListOrganizationsResponse } from 'services/api';
import { BaseApi } from 'services/api/base-api';
import { ApiResult } from 'services/api/result.type';
import { ListEventsResponse } from '../events/dto';
import { ListFacilitiesResponse } from '../organizations/dto/list-facilities.dto';
import {
  ExploreCampusesResponse,
  GetSchoolResponse,
  ListSchoolsResponse,
  SaveSchoolRequest,
  SaveSchoolResponse,
  UpdateSchoolRequest,
} from './dto';
import { CreateSchoolRequest } from './dto/create-school.request.dto';
import { GetSchoolStatsResponse } from './dto/get-school-stats.dto';
import { SchoolApprovalTitleDto } from './dto/school-approval-title.dto';
import { ApprovalTitleWithSequencesDto, UpdateApprovalTitleOrderDto } from './dto/approval-title-with-sequences.dto';
import { ApprovalFlowOrderResponse, UpdateApprovalFlowOrderRequest } from './dto/approval-flow-order.dto';

export class SchoolsApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.getSchool = this.getSchool.bind(this);
    this.getStats = this.getStats.bind(this);
    this.createSchool = this.createSchool.bind(this);
    this.saveSchool = this.saveSchool.bind(this);
    this.listSchools = this.listSchools.bind(this);
    this.suggestedSchools = this.suggestedSchools.bind(this);
    this.suggestedOrganizations = this.suggestedOrganizations.bind(this);
    this.listOrganizations = this.listOrganizations.bind(this);
    this.getSchoolEvents = this.getSchoolEvents.bind(this);
    this.exploreCampuses = this.exploreCampuses.bind(this);
  }

  public async getSchool (schoolId: string): ApiResult<GetSchoolResponse> {
    return await this.get<GetSchoolResponse>(`/schools/${schoolId}`);
  }

  public async getStats (schoolId: string): ApiResult<GetSchoolStatsResponse> {
    return await this.get<GetSchoolStatsResponse>(`/schools/${schoolId}/stats`);
  }

  public async createSchool (
    data: CreateSchoolRequest,
  ): ApiResult<{ id: string }> {
    return await this.post<{ id: string }>('/schools', data);
  }

  public async saveSchool (
    schoolId: string,
    data: SaveSchoolRequest | UpdateSchoolRequest,
  ): ApiResult<SaveSchoolResponse> {
    return await this.patch<SaveSchoolResponse>(`/schools/${schoolId}`, data);
  }

  public async hideSchool (schoolId: string): ApiResult<void> {
    return await this.post<void>(`/schools/${schoolId}/hide`);
  }

  public async unhideSchool (schoolId: string): ApiResult<void> {
    return await this.post<void>(`/schools/${schoolId}/unhide`);
  }

  async listSchools ({
    page,
    pageSize,
    search,
    cursor,
  }: {
    page?: number;
    pageSize?: number;
    search?: string | null;
    cursor?: string | null;
  }): ApiResult<ListSchoolsResponse> {
    return await this.get<ListSchoolsResponse>('/schools', {
      params: {
        page,
        pageSize,
        search,
        cursor,
      },
    });
  }

  async suggestedSchools ({
    page,
    pageSize,
    search,
  }: {
    page?: number;
    pageSize?: number;
    search?: string | null;
  } = {}): ApiResult<ListSchoolsResponse> {
    return await this.get<ListSchoolsResponse>('/schools/suggested', {
      params: {
        page,
        pageSize,
        search,
      },
    });
  }

  async exploreCampuses (): ApiResult<ExploreCampusesResponse> {
    return await this.get<ExploreCampusesResponse>('/schools/explore-schools');
  }

  async deleteSchool (id: string): ApiResult<void> {
    return await this.delete(`/schools/${id}`);
  }

  public async suggestedOrganizations (
    slug: string,
  ): ApiResult<ListOrganizationsResponse> {
    return await this.get<ListOrganizationsResponse>(
      `/schools/${slug}/organizations/suggested`,
    );
  }

  public async listOrganizations (
    schoolId: string,
    {
      page,
      pageSize,
      search,
      cursor,
      followed,
      minimal,
      includeInactive,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      cursor?: string | null;
      followed?: boolean;
      minimal?: boolean;
      includeInactive?: boolean;
    },
  ): ApiResult<ListOrganizationsResponse> {
    return await this.get<ListOrganizationsResponse>(
      `/schools/${schoolId}/organizations`,
      {
        params: {
          page,
          pageSize,
          search,
          cursor,
          followed: followed ? 'true' : undefined,
          minimal: minimal ? 'true' : undefined,
          includeInactive: includeInactive ? 'true' : undefined,
        },
      },
    );
  }

  public async getSchoolEvents (
    slug: string,
    {
      page,
      pageSize,
      search,
      statuses,
      categories,
      setting,
      organization,
      cursor,
      orderBy,
      timeFilter,
      now,
      windowDays,
    }: {
      page?: number;
      pageSize?: number;
      search?: string | null;
      statuses?: EventStatus[];
      categories?: Category[];
      setting?: SettingApi;
      organization?: string | 'followed';
      cursor?: string | null;
      orderBy?: 'views';
      timeFilter?: 'recent_only' | 'upcoming';
      now?: string;
      windowDays?: number;
    },
  ): ApiResult<ListEventsResponse> {
    return await this.get<ListEventsResponse>(`/schools/${slug}/events`, {
      params: {
        page,
        pageSize,
        search,
        statuses,
        categories,
        setting,
        organization,
        cursor,
        orderBy,
        timeFilter,
        now,
        windowDays,
      },
    });
  }

  public async getSchoolFacilities (schoolId: string): ApiResult<ListFacilitiesResponse> {
    return await this.get<ListFacilitiesResponse>(`/schools/${schoolId}/facilities`);
  }

  public async getSchoolFacilitiesSimple (schoolId: string): ApiResult<{
    facilities: Array<{
      id: string;
      name: string;
    }>;
  }> {
    return await this.get<{
      facilities: Array<{
        id: string;
        name: string;
      }>;
    }>(`/schools/${schoolId}/facilities-simple`);
  }

  public async getSchoolResidenceHalls (schoolId: string): ApiResult<{
    residenceHalls: Array<{
      id: string;
      name: string;
    }>;
  }> {
    return await this.get<{
      residenceHalls: Array<{
        id: string;
        name: string;
      }>;
    }>(`/schools/${schoolId}/residence-halls`);
  }

  public async createFacility (
    schoolId: string,
    data: {
      name: string;
      approverIds?: string[];
      isResidenceHall?: boolean;
    },
  ): ApiResult<{ id: string }> {
    return await this.post<{ id: string }>(`/schools/${schoolId}/facilities`, data);
  }

  public async updateFacility (
    facilityId: string,
    data: {
      name?: string;
      approverIds?: string[];
      isResidenceHall?: boolean;
    },
  ): ApiResult<void> {
    return await this.patch<void>(`/schools/facilities/${facilityId}`, data);
  }

  public async deleteFacility (facilityId: string): ApiResult<void> {
    return await this.delete<void>(`/schools/facilities/${facilityId}`);
  }

  public async getLearningOutcomes (
    schoolId: string,
    {
      page,
      pageSize,
      search,
    }: {
      page?: number;
      pageSize?: number;
      search?: string;
    },
  ): ApiResult<{
    count: number;
    learningOutcomes: Array<{
      id: string;
      title: string;
      description: string;
      naceCompetencies: string[];
      createdAt: string;
      updatedAt: string;
    }>;
  }> {
    return await this.get<{
      count: number;
      learningOutcomes: Array<{
        id: string;
        title: string;
        description: string;
        naceCompetencies: string[];
        createdAt: string;
        updatedAt: string;
      }>;
    }>(`/schools/${schoolId}/learning-outcomes`, {
      params: { page, pageSize, search },
    });
  }

  public async createLearningOutcome (
    schoolId: string,
    data: {
      title: string;
      description: string;
      naceCompetencies?: string[];
    },
  ): ApiResult<{ id: string }> {
    return await this.post<{ id: string }>(`/schools/${schoolId}/learning-outcomes`, data);
  }

  public async updateLearningOutcome (
    learningOutcomeId: string,
    data: {
      title?: string;
      description?: string;
      naceCompetencies?: string[];
    },
  ): ApiResult<void> {
    return await this.patch<void>(`/schools/learning-outcomes/${learningOutcomeId}`, data);
  }

  public async deleteLearningOutcome (
    learningOutcomeId: string,
  ): ApiResult<void> {
    return await this.delete<void>(`/schools/learning-outcomes/${learningOutcomeId}`);
  }

  public async getApprovalSequences (schoolId: string): ApiResult<ApprovalTitleWithSequencesDto[]> {
    return await this.get<ApprovalTitleWithSequencesDto[]>(`/schools/${schoolId}/approval-sequences`);
  }

  public async addApprovalSequence (schoolId: string, data: {
    emailAddress: string;
    schoolApprovalTitleId: string;
  }): ApiResult<void> {
    return await this.post<void>(`/schools/${schoolId}/approval-sequences`, data);
  }

  public async removeApprovalSequence (schoolId: string, approvalSequenceId: string): ApiResult<void> {
    return await this.delete<void>(`/schools/${schoolId}/approval-sequences/${approvalSequenceId}`);
  }

  public async updateApprovalSequence (approvalSequenceId: string, data: {
    schoolApprovalTitleId: string;
  }): ApiResult<void> {
    return await this.put<void>(`/schools/approval-sequences/${approvalSequenceId}`, data);
  }

  public async updateApprovalTitleOrder (schoolId: string, data: UpdateApprovalTitleOrderDto[]): ApiResult<void> {
    return await this.put<void>(`/schools/${schoolId}/approval-titles/order`, data);
  }

  public async getSchoolApprovalTitles (schoolId: string): ApiResult<SchoolApprovalTitleDto[]> {
    return await this.get<SchoolApprovalTitleDto[]>(`/schools/${schoolId}/approval-titles`);
  }

  public async getApprovalFlowOrder (schoolId: string): ApiResult<ApprovalFlowOrderResponse> {
    return await this.get<ApprovalFlowOrderResponse>(`/schools/${schoolId}/approval-flow-order`);
  }

  public async updateApprovalFlowOrder (
    schoolId: string,
    data: UpdateApprovalFlowOrderRequest,
  ): ApiResult<ApprovalFlowOrderResponse> {
    return await this.patch<ApprovalFlowOrderResponse>(`/schools/${schoolId}/approval-flow-order`, data);
  }

  public async getSchoolMajors (schoolId: string): ApiResult<{ majors: string[] }> {
    return await this.get<{ majors: string[] }>(`/schools/${schoolId}/majors`);
  }

  public async getSchoolMajorMappings (schoolId: string): ApiResult<{
    mappings: Array<{ id: string; majorId: string; majorName: string }>;
  }> {
    return await this.get<{
      mappings: Array<{ id: string; majorId: string; majorName: string }>;
    }>(`/schools/${schoolId}/major-mappings`);
  }

  public async getAvailableMajors (schoolId: string): ApiResult<{
    majors: Array<{ id: string; name: string }>;
  }> {
    return await this.get<{
      majors: Array<{ id: string; name: string }>;
    }>(`/schools/${schoolId}/available-majors`);
  }

  public async createSchoolMajorMapping (
    schoolId: string,
    majorId: string,
  ): ApiResult<{ id: string }> {
    return await this.post<{ id: string }>(`/schools/${schoolId}/major-mappings`, { majorId });
  }

  public async deleteSchoolMajorMapping (mappingId: string): ApiResult<void> {
    return await this.delete<void>(`/schools/major-mappings/${mappingId}`);
  }

  public async bulkDeleteSchoolMajorMappings (
    schoolId: string,
    mappingIds: string[],
  ): ApiResult<{ deleted: number }> {
    return await this.post<{ deleted: number }>(
      `/schools/${schoolId}/major-mappings/bulk-delete`,
      { mappingIds },
    );
  }
}
